import jsQR, { QRCode } from "jsqr";
import { theme } from "../theme";
import { User } from "../types/interfaces";
import {
  ButtonSeverity,
  DownloadFileTypes,
  EncryptionType,
  QrType,
} from "../types/types";

export const getUpiPatternForQR = (upi: string, name?: string): string => {
  const upiPattern = `upi://pay?pa=${upi}&pn=${
    name || ""
  }&mc=&tid=&tn=&am=&cu=INR`;
  return upiPattern;
};

export const decodeUpiPattern = (
  pattern: string
): {
  UPIId: string;
  name: string;
} => {
  const upiPattern = pattern?.split("&");
  const upiId = upiPattern && upiPattern[0]?.split("=")[1];
  const name = upiPattern && upiPattern[1]?.split("=")[1];
  return { UPIId: upiId, name };
};

export const getPhonePatternForQR = (phone: string): string => {
  return `TEL:+91${phone}`;
};

export const decodePhonePattern = (pattern: string): string => {
  return pattern?.split(":")[1]?.replace("+91", "");
};

export const getGeoLocationPatternForQR = (
  lat: string,
  lng: string
): string => {
  return "http://maps.google.com/maps?q=" + lat + "," + lng;
};

export const decodeGeoLocationPattern = (
  pattern: string
): { lat: string; lng: string } => {
  let latLng = pattern?.split("=")[1];
  let latLngArr = latLng?.split(",");
  return {
    lat: latLngArr && latLngArr[0],
    lng: latLngArr && latLngArr[1],
  };
};

export const getCalendarPatternForQR = (
  dateStartFormatted: string,
  dateEndFormatted: string,
  title: string
): string => {
  return `BEGIN:VCALENDAR\nBEGIN:VEVENT\nDTSTART:${dateStartFormatted}\nDTEND:${dateEndFormatted}\nSUMMARY:${title}\nEND:VEVENT\nEND:VCALENDAR`;
};

export const getMailPatternForQR = (
  to: string,
  subject: string,
  message: string
): string => {
  let msg = encodeURIComponent(message);
  return `MATMSG:TO:${to};SUB:${subject};BODY:${msg};`;
};

export const decodeMailPattern = (
  pattern: string
): {
  mailId: string;
  subject: string;
  message: string;
} => {
  const patternArray = pattern?.split(";");
  const mailId = patternArray && patternArray[0]?.split(":")[2];
  const subject = patternArray && patternArray[1]?.split(":")[1];
  const message =
    patternArray &&
    patternArray[2] &&
    patternArray[2]?.split(":")[1] &&
    decodeURIComponent(patternArray[2]?.split(":")[1]);
  return {
    mailId,
    subject,
    message,
  };
};

export const getSMSPatternForQR = (to: string, message: string): string => {
  let msg = encodeURIComponent(message);
  return `SMSTO:+91${to}:${msg}`;
};

export const decodeSMSPattern = (
  pattern: string
): { phone: string; message: string } => {
  let _pattern = pattern?.split(":");
  return {
    phone: _pattern && _pattern[1].replace("+91", ""),
    message: _pattern && _pattern[2] && decodeURIComponent(_pattern[2]),
  };
};

export const getWhatsappPatternForQR = (
  to: string,
  message: string
): string => {
  let msg = encodeURIComponent(message);
  return `api.whatsapp.com/send?phone=+91${to}&text=${msg}`;
};

export const decodeWhatAppPattern = (
  url: string
): {
  phone: string;
  message: string;
} => {
  let pattern = url?.split("?")[1];
  let params = pattern?.split("&");
  let phone = params && params[0]?.split("=")[1]?.replace("+91", "");
  let message =
    params &&
    params[1] &&
    params[1]?.split("=")[1] &&
    decodeURIComponent(params[1]?.split("=")[1]);
  return { phone, message };
};

export const getWiFiPatternForQR = (
  authentication: "WPA/WPA2" | "WEP",
  password: string,
  name: string,
  hidden: boolean
): string => {
  return `WIFI:T:${
    authentication === "WPA/WPA2" ? authentication?.split("/") && "/"[0] : "WEP"
  };S:${name};P:${password};H:${hidden};`;
};

export const decodeWiFiPattern = (
  wifiPattern: string
): {
  encryption: EncryptionType;
  password: string;
  name: string;
  hidden: boolean;
} => {
  let wifi = wifiPattern?.split(";");
  let encryption: EncryptionType =
    wifi && wifi[0]?.split(":")[2] === "WEP" ? "WEP" : "WPA/WPA2";
  let name = wifi && wifi[1]?.split(":")[1];
  let password = wifi && wifi[2]?.split(":")[1];
  let hidden = wifi && wifi[3]?.split(":")[1];
  return {
    encryption,
    password,
    name,
    hidden: hidden === "true",
  };
};

export const getYouTubePatternForQR = (videoId: string): string => {
  return `https://www.youtube.com/watch?v=${videoId}`;
};

export const decodeYouTubePattern = (url: string) => {
  let videoId = url?.split("=")[1];
  if (!videoId) {
    return null;
  }
  return videoId;
};

export const getBase64OfImage = (image: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(image);
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = (error) => {
      reject(error);
    };
  });
};

export const downloadSvg = (
  svg: Node,
  fileType: DownloadFileTypes,
  fileName: string
) => {
  const canvas = document.createElement("canvas");
  const w = 500;
  const h = 500;

  let img = new Image(),
    serializer = new XMLSerializer(),
    svgStr = serializer.serializeToString(svg);
  img.src = "data:image/svg+xml;base64," + window.btoa(svgStr);
  img.crossOrigin = "anonymous";

  if (fileType === "image/svg+xml") {
    let downloadLink = document.createElement("a");
    downloadLink.href = img.src;
    downloadLink.download = fileName;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    return;
  }

  canvas.width = w;
  canvas.height = h;
  let context = canvas.getContext("2d");
  img.onload = function () {
    if (context) {
      context.drawImage(img, 0, 0, w, h);
      const imgUrl = canvas.toDataURL(fileType);

      let downloadLink = document.createElement("a");
      downloadLink.dataset.downloadurl = [
        fileType,
        downloadLink.download,
        downloadLink.href,
      ].join(":");
      downloadLink.href = imgUrl;
      downloadLink.download = fileName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  };
};

export const downloadImageQR = (
  fileType: DownloadFileTypes,
  imageUrl: string,
  fileName: string
) => {
  fetch(imageUrl)
    .then((response) => response.text())
    .then((_svg) => {
      let SVGContainer = document.createElement("div");
      SVGContainer.style.display = "none";
      SVGContainer.innerHTML = _svg;
      let svg = SVGContainer.firstElementChild! as Node;
      downloadSvg(svg, fileType, fileName);
    });
};

export const downloadQR = (
  fileType: DownloadFileTypes,
  fileName: string,
  id: string
) => {
  const svg = document.getElementById(id)! as HTMLOrSVGImageElement;
  downloadSvg(svg, fileType, fileName);
};

export const convertImageFromBase64 = (
  fileType: DownloadFileTypes,
  fileName: string
) => {
  const svg = document.getElementById("qr-img")! as HTMLElement;
  var s = new XMLSerializer().serializeToString(svg);
  const encodedData = window.btoa(s);

  return new File(["data:image/svg+xml;base64," + encodedData], fileName, {
    type: fileType,
  });
};

export const getColorFromTheme = (
  active: boolean,
  severity: ButtonSeverity
) => {
  switch (severity) {
    case "primary":
      return active
        ? theme.backgroundColor.primaryBlack
        : theme.backgroundColor.primaryWhite;
    case "disabled":
      return theme.backgroundColor.secondaryWhite;
    case "warning":
      return theme.backgroundColor.primaryYellow;
    case "danger":
      return theme.backgroundColor.primaryRed;
    default:
      return active
        ? theme.backgroundColor.primaryBlack
        : theme.backgroundColor.primaryWhite;
  }
};

export const getBorderColorFromTheme = (
  active: boolean,
  severity: ButtonSeverity
) => {
  switch (severity) {
    case "primary":
      return theme.backgroundColor.primaryBlack;
    case "disabled":
      return theme.backgroundColor.secondaryWhite;
    case "warning":
      return theme.backgroundColor.primaryYellow;
    case "danger":
      return theme.backgroundColor.primaryRed;
    default:
      return theme.backgroundColor.primaryBlack;
  }
};

export const getHoverColorFromTheme = (
  active: boolean,
  severity: ButtonSeverity
) => {
  switch (severity) {
    case "primary":
      return theme.backgroundColor.primaryBlack;
    case "disabled":
      return theme.backgroundColor.secondaryWhite;
    case "warning":
      return theme.backgroundColor.hoverYellow;
    case "danger":
      return theme.backgroundColor.hoverRed;
    default:
      return theme.backgroundColor.primaryBlack;
  }
};

export const getTextColorFromTheme = (
  active: boolean,
  severity: ButtonSeverity
) => {
  switch (severity) {
    case "primary":
      return active
        ? theme.backgroundColor.primaryWhite
        : theme.backgroundColor.primaryBlack;
    default:
      return theme.backgroundColor.primaryWhite;
  }
};

/**
 * QR Decoder function
 * @description Changes fill color of svg paths temporarily and decodes the QR using `jsQR` library
 */
export const decodeQrFromImageSrc = (
  src: string,
  callback: (codeObject: QRCode | null) => void
) => {
  const width = 1000;
  const height = 1000;
  try {
    fetch(src)
      .then((response) => response.text())
      .then((_svg) => {
        let SVGContainer = document.createElement("div");
        SVGContainer.style.display = "none";
        SVGContainer.innerHTML = _svg;
        let svg: any = SVGContainer.firstElementChild; // get svg from src
        let paths = svg.getElementsByTagName("path");
        let colors = ["#fff", "#000"];
        // change fill color to black and white so that it can be decoded even if it is colored
        for (let i = 0; i < paths.length; i++) {
          paths[i].setAttribute("fill", colors[i]);
        }
        const serializer = new XMLSerializer();
        const svgXmlString = window.btoa(serializer.serializeToString(svg));
        let img = new Image();
        img.src = "data:image/svg+xml;base64," + svgXmlString;
        const canvas = document.createElement("canvas")! as HTMLCanvasElement;
        canvas.width = width;
        canvas.height = height;

        const context = canvas.getContext("2d");
        if (context) {
          img.onload = function () {
            context.drawImage(img, 0, 0, width, height);
            const pixels = context.getImageData(0, 0, width, height);
            const imageData = pixels.data;
            const code = jsQR(imageData, width, height, {
              inversionAttempts: "dontInvert",
            });
            callback(code);
          };
          img.onerror = function () {
            callback(null);
          };
        }
      })
      .catch((err) => {
        console.log("SVF FETCH ERROR: ", err);
        callback(null);
      });
  } catch (error) {
    callback(null);
  }
};

export const toggleHeader = (show: boolean) => {
  let header = document.getElementById("mobile-header");
  if (header) {
    header.style.display = show ? "flex" : "none";
  }
};

export const isValidEmail = (email: string) => {
  const re =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; // eslint-disable-line
  return re.test(String(email).trim().toLowerCase());
};

export const isValidPhoneNumber = (phoneNumber: string) => {
  const re = /^[6-9]\d{9}$/;
  return re.test(String(phoneNumber).trim().toLowerCase());
};

export const handleQrFormValidations = (QrType: QrType, value: string) => {
  let error: any;
  let hasError = false;
  if (QrType === "text" && !value.trim()) {
    error = "Please enter a text";
    hasError = true;
  }
  if (QrType === "url" && !value.trim()) {
    error = "Please enter a url";
    hasError = true;
  }

  if (QrType === "email" && !value.trim()) {
    error = "Please enter an email";
    hasError = true;
  }
  if (QrType === "phone" && !decodePhonePattern(value)) {
    error = "Please enter a phone number";
    hasError = true;
  } else if (
    QrType === "phone" &&
    !isValidPhoneNumber(decodePhonePattern(value))
  ) {
    error = "Please enter a valid phone number";
    hasError = true;
  }
  if (QrType === "sms") {
    let objSms = { phoneError: "", messageError: "" };
    let decodedSms = decodeSMSPattern(value);
    if (!decodedSms.phone) {
      objSms.phoneError = "Please enter a phone number";
      hasError = true;
    } else if (!isValidPhoneNumber(decodedSms.phone)) {
      objSms.phoneError = "Please enter a valid phone number";
      hasError = true;
    }

    if (!decodedSms.message.trim()) {
      objSms.messageError = "Please enter a message";
      hasError = true;
    }
    error = { ...objSms };
  }
  if (QrType === "whatsapp") {
    let objWa = { phoneError: "", messageError: "" };
    let decodedWhatsapp = decodeWhatAppPattern(value);
    if (!decodedWhatsapp.phone) {
      objWa.phoneError = "Please enter a whats app number";
      hasError = true;
    } else if (!isValidPhoneNumber(decodedWhatsapp.phone)) {
      objWa.phoneError = "Please enter a valid phone number";
      hasError = true;
    }
    if (!decodedWhatsapp.message) {
      objWa.messageError = "Please enter a message";
      hasError = true;
    }
    error = { ...objWa };
  }
  if (QrType === "phone") {
    let decodedPhone = decodePhonePattern(value);
    if (!decodedPhone) {
      error = "Please enter a phone number";
      hasError = true;
    } else if (!isValidPhoneNumber(decodedPhone)) {
      error = "Please enter a valid phone number";
      hasError = true;
    }
  }
  if (QrType === "email") {
    let objMail = { mailIdError: "", subjectError: "", bodyError: "" };

    let decodedEmail = decodeMailPattern(value);
    if (!decodedEmail.mailId.trim()) {
      objMail.mailIdError = "Please enter an email";
      hasError = true;
    } else if (!isValidEmail(decodedEmail.mailId)) {
      objMail.mailIdError = "Please enter a valid email";
      hasError = true;
    }
    if (!decodedEmail.subject.trim()) {
      objMail.subjectError = "Please enter a subject";
      hasError = true;
    }
    if (!decodedEmail.message.trim()) {
      objMail.bodyError = "Please enter a message";
      hasError = true;
    }
    error = { ...objMail };
  }

  if (QrType === "wifi") {
    let objWifi = { nameError: "", passwordError: "" };
    let decodedWifi = decodeWiFiPattern(value);
    if (!decodedWifi.name.trim()) {
      objWifi.nameError = "Please enter a name (ssid)";
      hasError = true;
    }
    if (!decodedWifi.password) {
      objWifi.passwordError = "Please enter a password";
      hasError = true;
    }
    error = { ...objWifi };
  }

  if (QrType === "location") {
    let objLatLong = { latError: "", lngError: "" };
    let decodedLatLong = decodeGeoLocationPattern(value);
    if (!decodedLatLong.lat.trim()) {
      objLatLong.latError = "Please enter a latitude";
      hasError = true;
    }
    if (!decodedLatLong.lng.trim()) {
      objLatLong.lngError = "Please enter a longitude";
      hasError = true;
    }
    error = { ...objLatLong };
  }
  if (QrType === "upi") {
    let objUpi = { upiIdError: "", nameError: "" };
    let decodedUpi = decodeUpiPattern(value);
    if (!decodedUpi.UPIId.trim()) {
      objUpi.upiIdError = "Please enter a UPI ID";
      hasError = true;
    }
    if (!decodedUpi.name.trim()) {
      objUpi.nameError = "Please enter a name";
      hasError = true;
    }
    error = { ...objUpi };
  }

  return { hasError, error };
};

export const isPaidUser = (user: User | null) => {
  if (!user) {
    return { isPaid: false, plan: "" };
  }
  if (["six_months", "twelve_months"].includes(user.currentPlan)) {
    return { isPaid: true, plan: user.currentPlan };
  }
  return { isPaid: false, plan: user.currentPlan };
};

export const goToPricing = () => {
  if (window.location.pathname === "/") {
    let element = document.getElementById(`pricing_section`);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  } else {
    window.location.href = "/#pricing_section";
  }
};
